/**
 * Tablero de planificación del mes: campañas (filas) × días (columnas).
 *
 * Lógica PURA, sin Supabase y sin `Date`: todas las fechas se manejan como cadenas
 * `YYYY-MM-DD`, así que la zona horaria del navegador no puede correr un día. Quien lea
 * de la base ya trae las fechas tal como están guardadas (hora de Lima).
 *
 * Cada celda dice "logrado / programado". El significado de "logrado" depende de la métrica:
 * antes del día interesa si hay gente puesta (`asignacion`); durante y después, si la gente
 * que se puso fue (`asistencia`).
 */

export type Metrica = 'asignacion' | 'asistencia';
export type EstadoCelda = 'sin_programacion' | 'nadie' | 'parcial' | 'completo';

/** Una posición del mes, ya aplanada, con lo necesario para contar. */
export interface PosicionPlan {
  campaignId: string;
  date: string;            // YYYY-MM-DD
  tieneAprobada: boolean;  // hay assignment en estado 'aprobada'
  marcoIngreso: boolean;   // ese assignment tiene attendance kind='ingreso'
}

export interface Celda {
  date: string;
  programado: number;
  logrado: number;
  estado: EstadoCelda;
}

export interface FilaCampania {
  campaignId: string;
  celdas: Record<string, Celda>;   // por fecha
  totalProgramado: number;
  totalLogrado: number;
  diasConProgramacion: number;
  porcentaje: number;              // 0-100, redondeado a entero
}

export interface Tablero {
  dias: string[];                  // todas las fechas del mes, en orden
  filas: FilaCampania[];
  totalesPorDia: Record<string, Celda>;
  granTotal: { programado: number; logrado: number; porcentaje: number };
}

const esBisiesto = (anio: number): boolean =>
  (anio % 4 === 0 && anio % 100 !== 0) || anio % 400 === 0;

const DIAS_POR_MES = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** Cantidad de días del mes (1..12), con la regla de siglos del calendario gregoriano. */
function diasEnMes(anio: number, mes: number): number {
  if (!Number.isInteger(mes) || mes < 1 || mes > 12) {
    throw new Error(`Mes inválido: ${mes}. Se espera un número de 1 a 12.`);
  }
  return mes === 2 && esBisiesto(anio) ? 29 : DIAS_POR_MES[mes - 1];
}

const dosDigitos = (n: number): string => String(n).padStart(2, '0');

/** Todas las fechas del mes en orden, como `YYYY-MM-DD`. Son las columnas del tablero. */
export function diasDelMes(anio: number, mes: number): string[] {
  const total = diasEnMes(anio, mes);
  const prefijo = `${anio}-${dosDigitos(mes)}-`;
  return Array.from({ length: total }, (_, i) => prefijo + dosDigitos(i + 1));
}

/**
 * Color de la celda. `sin_programacion` NO es lo mismo que `nadie`: en el primer caso la
 * campaña no trabajaba ese día (celda gris y vacía); en el segundo había puestos y no se
 * llenó ninguno (celda roja con su número, que es lo que hay que ir a resolver).
 */
export function estadoCelda(programado: number, logrado: number): EstadoCelda {
  if (programado <= 0) return 'sin_programacion';
  if (logrado <= 0) return 'nadie';
  return logrado >= programado ? 'completo' : 'parcial';
}

/**
 * Porcentaje de avance, entero.
 * - 0 programado devuelve 0 (nunca divide por cero).
 * - Nunca devuelve 100 si falta aunque sea una posición: 465/466 muestra 99, no 100.
 *   Un "100%" falso hace que el coordinador dé por cerrado un día que todavía tiene un hueco.
 */
function porcentajeDe(programado: number, logrado: number): number {
  if (programado <= 0) return 0;
  if (logrado >= programado) return 100;
  return Math.min(Math.round((logrado / programado) * 100), 99);
}

/** Celda vacía: el día existe como columna pero esa campaña no tenía programación. */
const celdaVacia = (date: string): Celda =>
  ({ date, programado: 0, logrado: 0, estado: 'sin_programacion' });

const seLogro = (p: PosicionPlan, metrica: Metrica): boolean =>
  (metrica === 'asignacion' ? p.tieneAprobada : p.marcoIngreso);

/**
 * Agrega las posiciones del mes en la matriz campaña × día.
 *
 * Las posiciones de otros meses se descartan: el tablero solo muestra columnas del mes
 * pedido y sus totales deben cuadrar con esas columnas. Una campaña sin ninguna posición
 * dentro del mes no genera fila.
 */
export function construirTablero(
  posiciones: PosicionPlan[], anio: number, mes: number, metrica: Metrica,
): Tablero {
  const dias = diasDelMes(anio, mes);
  const delMes = new Set(dias);

  // campaignId -> fecha -> { programado, logrado }
  const conteos = new Map<string, Map<string, { programado: number; logrado: number }>>();
  for (const p of posiciones) {
    if (!delMes.has(p.date)) continue;
    let porFecha = conteos.get(p.campaignId);
    if (!porFecha) {
      porFecha = new Map();
      conteos.set(p.campaignId, porFecha);
    }
    const acc = porFecha.get(p.date) ?? { programado: 0, logrado: 0 };
    acc.programado += 1;
    if (seLogro(p, metrica)) acc.logrado += 1;
    porFecha.set(p.date, acc);
  }

  const totalesPorDia: Record<string, Celda> = {};
  for (const d of dias) totalesPorDia[d] = celdaVacia(d);

  const filas: FilaCampania[] = [];
  for (const [campaignId, porFecha] of conteos) {
    const celdas: Record<string, Celda> = {};
    let totalProgramado = 0;
    let totalLogrado = 0;
    let diasConProgramacion = 0;

    for (const d of dias) {
      const acc = porFecha.get(d);
      if (!acc) {
        celdas[d] = celdaVacia(d);
        continue;
      }
      celdas[d] = {
        date: d,
        programado: acc.programado,
        logrado: acc.logrado,
        estado: estadoCelda(acc.programado, acc.logrado),
      };
      totalProgramado += acc.programado;
      totalLogrado += acc.logrado;
      if (acc.programado > 0) diasConProgramacion += 1;

      const total = totalesPorDia[d];
      totalesPorDia[d] = {
        date: d,
        programado: total.programado + acc.programado,
        logrado: total.logrado + acc.logrado,
        estado: estadoCelda(total.programado + acc.programado, total.logrado + acc.logrado),
      };
    }

    filas.push({
      campaignId,
      celdas,
      totalProgramado,
      totalLogrado,
      diasConProgramacion,
      porcentaje: porcentajeDe(totalProgramado, totalLogrado),
    });
  }

  // Orden estable e independiente del orden en que llegaron las filas de la base.
  filas.sort((a, b) => a.campaignId.localeCompare(b.campaignId));

  const programado = filas.reduce((s, f) => s + f.totalProgramado, 0);
  const logrado = filas.reduce((s, f) => s + f.totalLogrado, 0);

  return {
    dias,
    filas,
    totalesPorDia,
    granTotal: { programado, logrado, porcentaje: porcentajeDe(programado, logrado) },
  };
}
